/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect } from 'react';
import styled from 'styled-components';

import { MediaQuery, useMediaQuery } from '@toucan/client/global/application';

import {
    TrustCardDirection,
    TrustedShopsBadgeConfig,
    TrustedShopsBadgeVariant,
    WindowWithTrustedShops,
    trustedShopsBadge,
} from './helpers/trusted-shops';
import { styles } from './trusted-shops-badge.styles';

export {
    type WindowWithTrustedShops,
    TrustCardDirection,
} from './helpers/trusted-shops';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
interface TrustedShopsBadgeProps {
    /**
     * Our unique trusted shops id
     */
    trustedShopsId: string;

    /**
     * A configuration object passed to the external Trusted Shops badge library
     */
    config?: Partial<TrustedShopsBadgeConfig>;

    /**
     * The Id that the trustbadge script should target
     */
    id: string;

    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;
}

const StyledTrustedShopsBadge = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const TrustedShopsBadge: FunctionComponent<TrustedShopsBadgeProps> = ({
    trustedShopsId,
    config = {},
    id,
    ...otherProps
}: TrustedShopsBadgeProps) => {
    const isSmallTablet = useMediaQuery(MediaQuery.SMALL_TABLET);
    const router = useRouter();

    const scriptId = 'trustedshopsScript';

    const defaultConfig: TrustedShopsBadgeConfig = {
        variant: TrustedShopsBadgeVariant.CUSTOM,
        customElementId: 'trustedShopsBadge',
        customCheckoutElementId: 'trustedShopsContainer',
        trustcardDirection: TrustCardDirection.BOTTOM_RIGHT,
        disableResponsive: 'true',
    };

    const trustedShopsBadgeConfig = {
        ...defaultConfig,
        ...config,
    };

    /**
     * Remove the trustedshops script from the dom
     */
    function removeScript() {
        (window as WindowWithTrustedShops)?.trustbadge?.remove();
        document.getElementById(scriptId)?.remove();
    }

    /**
     * Add a trustedshops script to the dom if it doesnt already exist
     *
     * @param {string} trustedShopsId - Our trusted shops id
     * @returns {void}
     */
    function addScript(trustedShopsId: string) {
        if (!document.getElementById(scriptId)) {
            const node = document.createElement('script');
            node.src = `https://widgets.trustedshops.com/js/${trustedShopsId}.js`;
            node.id = scriptId;
            document.body.appendChild(node);
        }
    }

    useEffect(() => {
        (window as WindowWithTrustedShops)._tsConfig = trustedShopsBadgeConfig;
        trustedShopsBadge.reInitialize();
    }, [isSmallTablet]);

    useEffect(() => {
        (window as WindowWithTrustedShops)._tsConfig = trustedShopsBadgeConfig;

        router.events.on('routeChangeStart', removeScript);
        router.events.on('routeChangeComplete', () =>
            addScript(trustedShopsId)
        );

        addScript(trustedShopsId);

        return () => {
            router.events.off('routeChangeStart', removeScript);
            router.events.off('routeChangeComplete', addScript);
        };
    }, []);

    return (
        <StyledTrustedShopsBadge
            id={id}
            {...otherProps}
        ></StyledTrustedShopsBadge>
    );
};
